/**
 * /academy/tutor — bilingual AI tutor with decreasing scaffolding (P3B-11): explanations in the
 * help language at the start, then in both languages, then in French only, following the level
 * (thresholds in the exam format table). The conversation stays on this device; English false
 * friends are pointed out in the learner's messages and in any text checked here.
 */

import {
  ApiError,
  type FalseFriendHit,
  type TutorMessageModeEnum,
  type TutorReply,
  academyTutorCreate,
  academyTutorFalseFriendsCreate,
  useAcademyTutorRetrieve,
} from "@agencyos/api-client";
import { Badge, Button, GlassCard, SegmentedControl, Skeleton, TextAreaField, buttonClass } from "@agencyos/ui-web";
import { Languages, MessagesSquare, SearchCheck, SendHorizontal, Sparkles, Trash2 } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { readStorage, writeStorage } from "../lib/storage";
import { FalseFriendsList } from "./FalseFriendsList";
import { bandText } from "./ielts/band";
import { IELTS, contentLanguage, examParams, useActiveExam } from "./ielts/exam";
import { InMyLanguage } from "./ielts/InMyLanguage";
import { CardTitle, PageHead } from "./ui";

import "./tutor.css";

const HISTORY_KEY = "academy.tutor.history";
const MODE_KEY = "academy.tutor.mode";
const MODES: TutorMessageModeEnum[] = ["auto", "english", "bilingual", "french"];
const MAX_TURNS = 40;

interface Turn {
  role: "learner" | "tutor";
  text: string;
  reply?: TutorReply;
}

function isTurn(value: unknown): value is Turn {
  if (typeof value !== "object" || value === null) return false;
  const turn = value as Record<string, unknown>;
  return (turn.role === "learner" || turn.role === "tutor") && typeof turn.text === "string";
}

/** The IELTS conversation is kept apart from the TCF one (D-105). */
const historyKey = (exam: string) => (exam === "tcf_canada" ? HISTORY_KEY : `${HISTORY_KEY}.${exam === IELTS ? "ielts" : exam}`);

function readHistory(key = HISTORY_KEY): Turn[] {
  try {
    const raw: unknown = JSON.parse(readStorage(key) ?? "[]");
    return Array.isArray(raw) ? raw.filter(isTurn).slice(-MAX_TURNS) : [];
  } catch {
    return [];
  }
}

function readMode(): TutorMessageModeEnum {
  const saved = readStorage(MODE_KEY);
  return MODES.find((m) => m === saved) ?? "auto";
}

function TutorBubble({ turn, examLang = "fr" }: { turn: Turn; examLang?: string }) {
  const { t } = useTranslation();
  const reply = turn.reply;
  const lang = reply?.language === "fr" || reply?.language === "en" || reply?.language === "de" ? reply.language : undefined;
  if (turn.role === "learner") {
    return (
      <div className="ac-msg ac-msg--me">
        <span className="visually-hidden">{t("academyTutor.chat.you")} </span>
        <div className="ac-bubble ac-bubble--me">{turn.text}</div>
      </div>
    );
  }
  return (
    <div className="ac-msg ac-msg--them">
      <span className="ac-msg__avatar" aria-hidden="true">
        <Sparkles size={15} />
      </span>
      <div className="ac-bubble ac-bubble--them">
        <span className="ac-bubble__who">{t("academyTutor.chat.tutor")}</span>
        <span lang={lang} className="ac-bubble__text">
          {turn.text}
        </span>
        {reply?.source === "fallback" && <span className="ac-footnote">{t("academyTutor.chat.unavailable")}</span>}
        {/* D-122: the advice in the mother tongue, the examples staying in the exam language */}
        {lang && <InMyLanguage text={turn.text} source={lang} examLanguage={examLang === "en" || examLang === "de" ? examLang : "fr"} kind="tutor" />}
        {reply && reply.corrections.length > 0 && (
          <div className="ac-block">
            <h4 className="ac-label">{t("academyTutor.chat.corrections")}</h4>
            <ul className="ac-errors">
              {reply.corrections.map((c, index) => (
                <li key={index} className="ac-error-row academy-error-row">
                  <p lang={examLang}>
                    <del>{c.excerpt}</del> → <ins>{c.correction}</ins>
                  </p>
                  {c.explanation && <p className="ac-footnote">{c.explanation}</p>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {reply && <FalseFriendsList hits={reply.false_friends} />}
      </div>
    </div>
  );
}

function TextCheck({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [hits, setHits] = useState<FalseFriendHit[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (event: FormEvent) => {
    event.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const response = await academyTutorFalseFriendsCreate({ text });
      setHits(response.data.hits);
    } catch (failure) {
      setError(failure instanceof ApiError && failure.detail ? failure.detail : t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <GlassCard as="section" className="ac-card" aria-labelledby="tutor-check">
      <form className="ac-form tutor-form" onSubmit={(event) => void run(event)}>
        <CardTitle id="tutor-check" icon={<SearchCheck size={16} />} tone="warning">
          {t("academyTutor.check.title")}
        </CardTitle>
        {enabled ? (
          <>
            <p className="ac-secondary">{t("academyTutor.check.intro")}</p>
            <TextAreaField label={t("academyTutor.check.label")} value={text} rows={5} lang="fr" onChange={(e) => setText(e.target.value)} />
            <div className="ac-card__actions">
              <Button type="submit" variant="secondary" disabled={busy || !text.trim()}>
                {busy ? t("common.loading") : t("academyTutor.check.run")}
              </Button>
            </div>
            {hits !== null &&
              (hits.length ? (
                <FalseFriendsList hits={hits} />
              ) : (
                <p className="ok" role="status">
                  {t("academyTutor.check.none")}
                </p>
              ))}
            {error && (
              <p className="ko" role="alert">
                {error}
              </p>
            )}
          </>
        ) : (
          <p className="ac-secondary">{t("academyTutor.check.disabled")}</p>
        )}
      </form>
    </GlassCard>
  );
}

export function TutorPage() {
  const { t } = useTranslation();
  const { exam } = useActiveExam();
  const ielts = exam === IELTS;
  const german = exam === "goethe"; // D-118: help language, then both, then German only
  const storageKey = historyKey(exam);
  // D-123: the texts of the exam's tutor (« your French tutor… the TCF » was shown for every exam)
  const ns = ielts ? "ielts" : exam === "tef_canada" ? "tef" : german ? "goethe" : "";
  const tt = (key: string) => t(ns ? [`${ns}.tutor.${key}`, `academyTutor.${key}`] : `academyTutor.${key}`);
  const state = useAcademyTutorRetrieve(examParams(exam), { query: { retry: false } });
  const tutor = state.data?.status === 200 ? state.data.data : undefined;
  const [turns, setTurns] = useState<Turn[]>(() => readHistory(storageKey));
  const [mode, setMode] = useState<TutorMessageModeEnum>(readMode);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => {
    writeStorage(storageKey, turns.length ? JSON.stringify(turns.slice(-MAX_TURNS)) : null);
    end.current?.scrollIntoView?.({ block: "nearest" });
  }, [turns, storageKey]);

  if (state.isLoading) return <Skeleton style={{ height: 320 }} />;
  if (state.error instanceof ApiError && state.error.status === 403) {
    return (
      <div className="ac-page">
        <div className="ac-surface ac-card ac-card--center ac-paywall">
          <span className="ac-challenge-hero__icon ac-tone--accent" aria-hidden="true">
            <MessagesSquare size={26} />
          </span>
          <h1 className="ac-title-2">{t("academyTutor.paywall.title")}</h1>
          <p className="ac-secondary">{t("academyTutor.paywall.text")}</p>
          <Link className={buttonClass("primary", { size: "lg" })} to="/academy/home">
            {t("academyTutor.paywall.cta")}
          </Link>
        </div>
      </div>
    );
  }
  if (!tutor) {
    return (
      <GlassCard className="ac-card ac-card--center">
        <p className="ko">{t("common.error")}</p>
        <Button variant="secondary" onClick={() => void state.refetch()}>
          {t("common.retry")}
        </Button>
      </GlassCard>
    );
  }

  const choosable = tutor.help_language !== "fr" && tutor.stages.length > 1;
  // The last answer tells the stage really used (the learner may have chosen another one).
  const lastReply = [...turns].reverse().find((turn) => turn.reply)?.reply;
  const shown = lastReply ?? tutor;
  const stageName = (stage: string) => {
    const known = MODES.find((m) => m === stage);
    return known ? t(`academyTutor.stage.${known}`) : t(`ielts.tutor.stages.${stage}`, { defaultValue: stage });
  };
  const options = MODES.filter((m) => m === "auto" || tutor.stages.includes(m)).map((m) => ({
    value: m,
    label: t(`academyTutor.stage.${m}`),
  }));

  const send = async (event: FormEvent) => {
    event.preventDefault();
    const text = message.trim();
    if (!text || busy) return;
    const history = turns.map(({ role, text: said }) => ({ role, text: said }));
    setTurns((previous) => [...previous, { role: "learner", text }]);
    setMessage("");
    setBusy(true);
    setError(null);
    try {
      const response = await academyTutorCreate({ message: text, history, mode: choosable ? mode : "auto", ...(exam !== "tcf_canada" ? { exam } : {}) });
      setTurns((previous) => [...previous, { role: "tutor", text: response.data.reply, reply: response.data }]);
    } catch (failure) {
      setError(failure instanceof ApiError && failure.detail ? failure.detail : t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ac-page">
      <PageHead
        eyebrow={exam !== "tcf_canada" ? `${t(`ielts.exams.${exam}.name`)} · ${t("academyTutor.eyebrow")}` : t("academyTutor.eyebrow")}
        title={tt("title")}
        subtitle={<p>{tt("intro")}</p>}
      />
      <p className="ac-note" role="note">
        {tt("rules")}
      </p>

      <div className="ac-grid ac-tutor">
        <section className="ac-chat ac-span-8" aria-labelledby="tutor-chat">
          <div className="ac-chat__head">
            <span className="ac-msg__avatar ac-msg__avatar--lg" aria-hidden="true">
              <Sparkles size={18} />
            </span>
            <div className="ac-chat__who">
              <h2 id="tutor-chat" className="ac-headline">
                {t("academyTutor.chat.title")}
              </h2>
              <p className="ac-caption">{t("academyTutor.chat.kept")}</p>
            </div>
            {turns.length > 0 && (
              <button type="button" className="ac-icon-button ac-icon-button--label" onClick={() => setTurns([])}>
                <Trash2 aria-hidden size={15} />
                <span>{t("academyTutor.chat.clear")}</span>
              </button>
            )}
          </div>
          <div className="ac-chat__log tutor-chat" aria-live="polite">
            {turns.length === 0 && (
              <div className="ac-chat__empty">
                <MessagesSquare aria-hidden size={26} />
                <p className="ac-secondary">{tt("chat.empty")}</p>
              </div>
            )}
            {turns.map((turn, index) => (
              <TutorBubble key={index} turn={turn} examLang={contentLanguage(exam)} />
            ))}
            {busy && (
              <div className="ac-msg ac-msg--them" role="status">
                <span className="ac-msg__avatar" aria-hidden="true">
                  <Sparkles size={15} />
                </span>
                <div className="ac-bubble ac-bubble--them ac-bubble--typing">
                  <span className="ac-typing" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </span>
                  <span className="visually-hidden">{t("academyTutor.chat.sending")}</span>
                </div>
              </div>
            )}
            <div ref={end} />
          </div>
          <form className="ac-composer tutor-form" onSubmit={(event) => void send(event)}>
            <TextAreaField
              label={t("academyTutor.chat.label")}
              placeholder={tt("chat.placeholder")}
              value={message}
              rows={2}
              maxLength={1500}
              onChange={(e) => setMessage(e.target.value)}
            />
            <Button type="submit" disabled={busy || !message.trim()} icon={<SendHorizontal aria-hidden size={16} />}>
              {busy ? t("common.loading") : t("academyTutor.chat.send")}
            </Button>
            {error && (
              <p className="ko" role="alert">
                {error}
              </p>
            )}
          </form>
        </section>

        <div className="ac-span-4 ac-side-stack">
          <GlassCard as="section" className="ac-card" aria-labelledby="tutor-stage">
            <CardTitle id="tutor-stage" icon={<Languages size={16} />} tone="success">
              {t("academyTutor.stage.title")}
            </CardTitle>
            <p role="status" className="ac-stage-line">
              <Badge tone="success">
                {german
                  ? t("goethe.tutor.current", { stage: t(`goethe.tutor.stages.${shown.stage}`, { defaultValue: stageName(shown.stage) }), percent: shown.german_percent ?? 0 })
                  : ielts
                  ? t("ielts.tutor.current", { stage: stageName(shown.stage), percent: shown.english_percent ?? 100 - shown.french_percent })
                  : t("academyTutor.stage.current", { stage: stageName(shown.stage), percent: shown.french_percent })}
              </Badge>
            </p>
            <div className="ac-french-meter" aria-hidden="true">
              <span style={{ transform: `scaleX(${Math.max(0, Math.min(100, german ? (shown.german_percent ?? 0) : ielts ? (shown.english_percent ?? 100 - shown.french_percent) : shown.french_percent)) / 100})` }} />
            </div>
            <p className="ac-footnote">
              {german
                ? tutor.level
                  ? t("goethe.tutor.level", { level: tutor.level })
                  : t("academyTutor.stage.noLevel")
                : ielts
                ? typeof tutor.band === "number"
                  ? t("ielts.tutor.level", { band: bandText(tutor.band) })
                  : t("academyTutor.stage.noLevel")
                : tutor.nclc !== null
                  ? t("academyTutor.stage.level", { nclc: tutor.nclc })
                  : t("academyTutor.stage.noLevel")}
            </p>
            {choosable && (
              <SegmentedControl<TutorMessageModeEnum>
                label={t("academyTutor.stage.choose")}
                value={mode}
                options={options}
                onChange={(value) => {
                  setMode(value);
                  writeStorage(MODE_KEY, value === "auto" ? null : value);
                }}
              />
            )}
          </GlassCard>
          {/* the false friends are those of French: no check for the IELTS or German */}
          {ielts || german ? null : <TextCheck enabled={tutor.false_friends} />}
        </div>
      </div>
    </div>
  );
}
